import ToggleButton from 'components/ToggleButton/ToggleButton';
import InputField from 'components/InputField/InputField';

import {
  ChannelConfig,
  MarketplaceConfig,
  SettingTypes,
} from "TypesAndInterfaces";
import { useLazyQuery } from '@apollo/client';
import { getMarketplaceBlacklistQuery, MarketplaceBlackLists } from 'pages/FirstMarketplace/mutations';
import { FirstMarketplaceContext } from './FirstMarketplace.context';
import { ChangeEvent, useContext, useEffect, useState } from "react";
import { displayError } from 'utils/displayNotification';
import BlacklistDropdown from './BlacklistDropdown';
import InputEmailField from 'components/InputEmailField/InputEmailField';

interface IProps {
  marketplaceConfig: MarketplaceConfig;
  marketplaceName: string;
  emailList: string;
  setMarketplaceConfig: Function;
  setMarketplaceName: Function;
  setEmailList: Function;
  handleStateUpdate: (
    e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>,
    number?: boolean,
    type?: SettingTypes,
    channelID?: number,
    toogleProperty?: string,
    toogleState?: boolean,
    routePrioritiesName?: string,
    routePriorities?: string[],
    marketplaceOperatorEmails?: string) => void;
  setSaveButtonDisabled: Function;
  useChannels: boolean;
  setUseChannels: Function;
  useBalancePlatforms: boolean;
  setUseBalancePlatforms: Function;
  copyChannels: ChannelConfig[];
  marketplaceId: number | undefined;
  usePreBalancePlatforms: boolean;
  setUsePreBalancePlatforms: Function;
  setPreBalancePlatformEvent: Function;
  isPreBalanceFlagEnabled: boolean;
}

const AccountSettings = ({
  marketplaceConfig,
  marketplaceName,
  emailList,
  setMarketplaceConfig,
  setMarketplaceName,
  setEmailList,
  handleStateUpdate,
  setSaveButtonDisabled,
  useChannels,
  setUseChannels,
  useBalancePlatforms,
  setUseBalancePlatforms,
  usePreBalancePlatforms,
  setUsePreBalancePlatforms,
  copyChannels,
  marketplaceId,
  setPreBalancePlatformEvent,
  isPreBalanceFlagEnabled
}: IProps) => {

  const { setShopBlacklist, setInvoiceBlacklist } = useContext(FirstMarketplaceContext);

  const [shops, setShops] = useState<number[]>(marketplaceConfig.blacklists?.shops || []);
  const [invoices, setInvoices] = useState<number[]>(marketplaceConfig.blacklists?.invoices || []);

  const checkIsLocaleValid = (e: any) => {
    const locale = /^[a-z]{2}-[A-Z]{2}$/;
    const value = e.currentTarget.value.trimStart();
    locale.test(value) ? setSaveButtonDisabled(false) : setSaveButtonDisabled(true);
  };

  const [getMarketplaceBlacklists] = useLazyQuery<
    { getMarketplaceBlacklists: { shops: number[], invoices: number[] } },
    { pagination: MarketplaceBlackLists }
  >(getMarketplaceBlacklistQuery, {
    fetchPolicy: "no-cache",
    onError: (e) => {
      displayError(e.message);
    },
  });

  useEffect(() => {
    if (marketplaceConfig.blacklists?.shops?.length !== 0) {
      setShops(marketplaceConfig.blacklists?.shops || [])
    }
  }, [marketplaceConfig.blacklists?.shops])

  useEffect(() => {
    if (marketplaceConfig.blacklists?.invoices?.length !== 0) {
      setInvoices(marketplaceConfig.blacklists?.invoices || [])
    }
  }, [marketplaceConfig.blacklists?.invoices])

  useEffect(() => {
    const elements: number[] = [];
    shops.forEach((element: number) => {
      elements.push(element)
    })
    setShopBlacklist(elements)
  }, [setShopBlacklist, shops]);

  useEffect(() => {
    const elements: number[] = [];
    invoices.forEach((element: number) => {
      elements.push(element)
    })
    setInvoiceBlacklist(elements)
  }, [setInvoiceBlacklist, invoices]);

  useEffect(() => {
    const inputEvent = {
      target: {
        value: ''
      }
    } as React.ChangeEvent<HTMLInputElement>;


    handleStateUpdate(inputEvent, false, SettingTypes.Account, undefined, undefined, undefined, undefined, undefined, "marketplaceOperatorEmails")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailList])

  useEffect(() => {
    if (useChannels && useBalancePlatforms) {
      setUseChannels(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useChannels, useBalancePlatforms]);

  const handleChannelsChange = (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) => {
    if (!useChannels) {
      setUseChannels(true);
      setUseBalancePlatforms(false);
      handleStateUpdate(e, false, SettingTypes.Account, undefined, "useChannels", true);
      handleStateUpdate(e, false, SettingTypes.Account, undefined, "useBalancePlatforms", false);
    } else {
      setUseChannels(false);
      handleStateUpdate(e, false, SettingTypes.Account, undefined, "useChannels", false);
    }
  };

  const handleBalancePlatformsChange = (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) => {
    if (!useBalancePlatforms) {
      setUseBalancePlatforms(true);
      setUseChannels(false);
      handleStateUpdate(e, false, SettingTypes.Account, undefined, "useBalancePlatforms", true);
      handleStateUpdate(e, false, SettingTypes.Account, undefined, "useChannels", false);
    } else {
      setUseBalancePlatforms(false);
      handleStateUpdate(e, false, SettingTypes.Account, undefined, "useBalancePlatforms", false);
    }
  };

  return <>
    <div className="inputs-wrapper__inputs">
      <p className="main-content__sidebar__title">Account Settings</p>
      <InputField
        label="Marketplace Name"
        placeholder="Enter Marketplace Name"
        name="marketplaceName"
        onChange={(e) => {
          setMarketplaceName(e.currentTarget.value)
          handleStateUpdate(e, false)
        }}
        value={marketplaceName}
        maxLength={60}
      />

      <InputEmailField
        label="Operators Emails"
        placeholder="Enter Mirakl operators emails separated by comma or press enter"
        name="marketplaceOperatorEmails"
        className="tag-input"
        id="tag-input"
        onChange={() => {
        }}
        defaultValue={emailList}
        type='email'
        multiple
        setEmails={setEmailList}
      />
      <span className="form-inputs-container">
        {marketplaceId && <BlacklistDropdown
          marketplaceId={marketplaceId}
          blackListName='invoices'
          blackListLabel='Invoice Blacklist'
          getMarketplaceBlacklists={getMarketplaceBlacklists}
          blacklist={invoices}
          setBlacklist={setInvoices}
        />}
        {marketplaceId && <BlacklistDropdown
          marketplaceId={marketplaceId}
          blackListName='shops'
          blackListLabel='Shop Blacklist'
          getMarketplaceBlacklists={getMarketplaceBlacklists}
          blacklist={shops}
          setBlacklist={setShops}
        />}
      </span>
      <span className="form-inputs-container">
        <InputField
          label="Maximum Invoice Retry Count"
          placeholder="Enter Maximum Invoice Retry Count"
          name="maxInvoiceRetryCount"
          onChange={(e) => handleStateUpdate(e, true, SettingTypes.Account)}
          numeric
          maxLength={2}
          value={marketplaceConfig.account.maxInvoiceRetryCount}
        />
        <InputField
          label="Mirakl HOP Questions Count"
          placeholder="Enter Number of Questions in HOP Page"
          name="noOfSellerQuestions"
          onChange={(e) => handleStateUpdate(e, true, SettingTypes.Account)}
          numeric
          maxLength={2}
          value={marketplaceConfig.account.noOfSellerQuestions}
        />
      </span>
      <span className="form-inputs-container">
        <InputField
          label="Hop return URL"
          placeholder="Enter Hop return URL"
          name="hopReturnUrl"
          onChange={(e) => handleStateUpdate(e, false, SettingTypes.Account)}
          value={marketplaceConfig.account.hopReturnUrl}
        />
        <InputField
          showHideButton={true}
          type="locale"
          label="Locale"
          id="locale"
          pattern={'[a-z]{2}-[A-Z]{2}'}
          title={'Locale (format: en-EN, de-DE)'}
          placeholder="Enter locale code"
          name="locale"
          onInput={(e) => checkIsLocaleValid(e)}
          onChange={(e) => handleStateUpdate(e, false, SettingTypes.Account)}
          value={marketplaceConfig.account.locale}
        />
      </span>
    </div>
    <div className="radio-validate-container">
      <div className="radio-container">
        <section>
          <div>
            <p>Manage Payment Blocked</p>
            <ToggleButton
              onChange={(e) => {
                handleStateUpdate(e, false, SettingTypes.Account, undefined, "manageBlockPaymentFlags", !marketplaceConfig.account.manageBlockPaymentFlags)
                const newConfig = {
                  ...marketplaceConfig,
                  account: {
                    ...marketplaceConfig.account,
                    manageBlockPaymentFlags: !marketplaceConfig.account.manageBlockPaymentFlags,
                  },
                };
                setMarketplaceConfig(newConfig);
              }}
              name="manageBlockPaymentFlags"
              checked={marketplaceConfig.account.manageBlockPaymentFlags}
              checkedText="enabled"
              uncheckedText="disabled"
            />
          </div>
          <div>
            <p>Use Subscription</p>
            <ToggleButton
              onChange={(e) => {
                handleStateUpdate(e, false, SettingTypes.Account, undefined, "useSubscription", !marketplaceConfig.account.useSubscription)
                setMarketplaceConfig((prev: MarketplaceConfig) => ({
                  ...prev,
                  account: {
                    ...prev.account,
                    useSubscription: !marketplaceConfig.account.useSubscription,
                  },
                }));
              }}
              name="useSubscription"
              checked={marketplaceConfig.account.useSubscription}
              checkedText="enabled"
              uncheckedText="disabled"
            />
          </div>
          <div>
            <p>Use Commision</p>
            <ToggleButton
              onChange={(e) => {
                handleStateUpdate(e, false, SettingTypes.Account, undefined, "useCommision", !marketplaceConfig.account.useCommision)
                setMarketplaceConfig((prev: MarketplaceConfig) => ({
                  ...prev,
                  account: {
                    ...prev.account,
                    useCommision: !marketplaceConfig.account.useCommision,
                  },
                }));
              }}
              name="useCommision"
              checked={marketplaceConfig.account.useCommision}
              checkedText="enabled"
              uncheckedText="disabled"
            />
          </div>
          <div>
            <p>Use Balance Platform</p>
            <ToggleButton
              onChange={handleBalancePlatformsChange}
              name="useBalancePlatforms"
              checked={useBalancePlatforms}
              checkedText="enabled"
              uncheckedText="disabled"
              disabled={copyChannels.length > 1}
            />
          </div>
          {isPreBalanceFlagEnabled && (
            <div>
              <p>Use Pre-Balance Platform</p>
              <ToggleButton
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setPreBalancePlatformEvent(e)
                  // Let the useEffect in FirstMarketplaceForm handle the state update
                  // Don't update usePreBalancePlatforms directly here to avoid double-toggle
                }}
                name="enablePreLiableCommissions"
                checked={usePreBalancePlatforms}
                checkedText="enabled"
                uncheckedText="disabled"
              />
            </div>
          )}
        </section>
        <section>
          <div>
            <p>Unsuspend New Shops After KYC Pass</p>
            <ToggleButton
              onChange={(e) => {
                handleStateUpdate(e, false, SettingTypes.Account, undefined, "unsuspendNewShops", !marketplaceConfig.account.unsuspendNewShops)
                setMarketplaceConfig((prev: MarketplaceConfig) => ({
                  ...prev,
                  account: {
                    ...prev.account,
                    unsuspendNewShops: !marketplaceConfig.account.unsuspendNewShops,
                  },
                }));
              }}
              name="unsuspendNewShops"
              checked={marketplaceConfig.account.unsuspendNewShops}
              checkedText="enabled"
              uncheckedText="disabled"
            />
          </div>
          <div>
            <p>Use Channels</p>
            <ToggleButton
              onChange={handleChannelsChange}
              name="useChannels"
              checked={useChannels}
              checkedText="enabled"
              uncheckedText="disabled"
              disabled={marketplaceConfig.account.useBalancePlatforms && copyChannels.length > 1}
            />
          </div>
          <div>
            <p>Use Mirakl SSO</p>
            <ToggleButton
              onChange={(e) => {
                handleStateUpdate(e, false, SettingTypes.Account, undefined, "miraklSsoEnabled", !marketplaceConfig.account.miraklSsoEnabled)
                setMarketplaceConfig((prev: MarketplaceConfig) => ({
                  ...prev,
                  account: {
                    ...prev.account,
                    miraklSsoEnabled:
                      !marketplaceConfig.account.miraklSsoEnabled,
                  },
                }));
              }}
              name="miraklSsoEnabled"
              checked={marketplaceConfig.account.miraklSsoEnabled}
              checkedText="enabled"
              uncheckedText="disabled"
            />
          </div>
          <div>
            <p>Only ID Reference for Beneficiary</p>
            <ToggleButton
              onChange={(e) => {
                handleStateUpdate(e, false, SettingTypes.Account, undefined, "onlyIdReferenceForBeneficiary", !marketplaceConfig.account.onlyIdReferenceForBeneficiary)
                setMarketplaceConfig((prev: MarketplaceConfig) => ({
                  ...prev,
                  account: {
                    ...prev.account,
                    onlyIdReferenceForBeneficiary:
                      !marketplaceConfig.account.onlyIdReferenceForBeneficiary,
                  },
                }));
              }}
              name="onlyIdReferenceForBeneficiary"
              checked={marketplaceConfig.account.onlyIdReferenceForBeneficiary ?? false}
              checkedText="enabled"
              uncheckedText="disabled"
            />
          </div>
        </section>
      </div>
    </div>
  </>
};

export default AccountSettings;
